import React, { useEffect, useMemo, useState, useId, useRef } from 'react';
import Modal from '../Modal/Modal';
import leadService from '../../services/lead.service';
import dictsService, { DictItem } from '../../services/dicts.service';
import { Lead, LeadCheckActiveResponse } from '../../types/lead.types';
import { showToast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Box, MoveRight, Sparkles } from 'lucide-react';
import './LeadCreateModal.css';
import { useNavigate } from 'react-router-dom';

const MAX_PHONE_DIGITS = 11;
const DEFAULT_STAGE_CODE = 'new_lead';

const normalizePhoneDigits = (input: string): string => {
  const raw = input.replace(/\D/g, '');
  if (!raw) return '';
  const withLeadingSeven = raw.startsWith('7') ? raw : raw.replace(/^8/, '7');
  const ensured = withLeadingSeven.startsWith('7') ? withLeadingSeven : `7${withLeadingSeven}`;
  return ensured.slice(0, MAX_PHONE_DIGITS);
};

const formatPhoneDisplay = (digits: string): string => {
  if (!digits) return '';
  const country = digits[0];
  const region = digits.slice(1, 4);
  const part1 = digits.slice(4, 7);
  const part2 = digits.slice(7, 9);
  const part3 = digits.slice(9, 11);

  let result = `+${country}`;
  if (region.length > 0) {
    result += ` (${region}`;
    if (region.length === 3) result += ')';
  }
  if (part1.length > 0) {
    result += region.length === 3 ? ` ${part1}` : part1;
  }
  if (part2.length > 0) {
    result += `-${part2}`;
  }
  if (part3.length > 0) {
    result += `-${part3}`;
  }
  return result;
};

const formatPhoneForCheck = (digits: string): string => {
  if (digits.length !== MAX_PHONE_DIGITS) return '';
  const region = digits.slice(1, 4);
  const part1 = digits.slice(4, 7);
  const part2 = digits.slice(7, 9);
  const part3 = digits.slice(9, 11);
  return `+${digits[0]} (${region}) ${part1}-${part2}-${part3}`;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (lead: Lead) => void;
}

const LeadCreateModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const { profile, ensureProfile } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    source_id: null as number | null,
    phone_model: '',
    memory_capacity: '',
    battery_capacity_max: '',
    phone_condition: '',
    has_box: false,
    assigned_to_user_id: null as number | null,
  });

  const [leadSources, setLeadSources] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingActive, setCheckingActive] = useState(false);
  const [activeCheckError, setActiveCheckError] = useState<string | null>(null);
  const [activeCheckResult, setActiveCheckResult] = useState<LeadCheckActiveResponse | null>(null);
  const [activeCheckTrigger, setActiveCheckTrigger] = useState(0);
  const lastCheckedPhoneRef = useRef<string>('');
  const activeCheckRequestIdRef = useRef(0);

  const handleActiveLeadOpen = (leadId: number) => {
    onClose();
    navigate(`/leads/${leadId}`);
  };

  const nameFieldId = useId();
  const phoneFieldId = useId();
  const sourceFieldId = useId();
  const modelFieldId = useId();
  const memoryFieldId = useId();
  const batteryFieldId = useId();
  const conditionFieldId = useId();
  const hasBoxFieldId = useId();

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      name: '',
      phone: '',
      source_id: null,
      phone_model: '',
      memory_capacity: '',
      battery_capacity_max: '',
      phone_condition: '',
      has_box: false,
      assigned_to_user_id: null,
    });
    setError(null);
    setSubmitting(false);
    setCheckingActive(false);
    setActiveCheckError(null);
    setActiveCheckResult(null);
    setActiveCheckTrigger(0);
    lastCheckedPhoneRef.current = '';
    void ensureProfile();

    const loadInitialData = async () => {
      setLoading(true);
      try {
        const sourcesRes = await dictsService.listDictItems('lead_sources');
        setLeadSources(sourcesRes);
        setForm((prev) => ({
          ...prev,
          source_id: sourcesRes[0]?.id ?? null,
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Ошибка загрузки данных';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadInitialData();
  }, [isOpen, ensureProfile]);

  useEffect(() => {
    if (!isOpen) return;
    if (!profile?.id) return;
    setForm((prev) => {
      if (prev.assigned_to_user_id === profile.id) {
        return prev;
      }
      return { ...prev, assigned_to_user_id: profile.id };
    });
  }, [isOpen, profile?.id]);

  useEffect(() => {
    if (!isOpen) return;

    const digits = form.phone;

    if (digits.length !== MAX_PHONE_DIGITS) {
      setActiveCheckResult(null);
      setActiveCheckError(null);
      setCheckingActive(false);
      lastCheckedPhoneRef.current = '';
      return;
    }

    if (lastCheckedPhoneRef.current === digits) {
      return;
    }

    const requestId = activeCheckRequestIdRef.current + 1;
    activeCheckRequestIdRef.current = requestId;

    setCheckingActive(true);
    setActiveCheckError(null);
    setActiveCheckResult(null);

    const formattedPhone = formatPhoneForCheck(digits);
    if (!formattedPhone) {
      setCheckingActive(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await leadService.checkActiveLeads(formattedPhone);
        if (cancelled || activeCheckRequestIdRef.current !== requestId) {
          return;
        }
        setActiveCheckResult(res);
        lastCheckedPhoneRef.current = digits;
      } catch (error) {
        if (cancelled || activeCheckRequestIdRef.current !== requestId) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Ошибка проверки активных заявок';
        setActiveCheckResult(null);
        setActiveCheckError(message);
        lastCheckedPhoneRef.current = digits;
      } finally {
        if (!(cancelled || activeCheckRequestIdRef.current !== requestId)) {
          setCheckingActive(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.phone, isOpen, activeCheckTrigger]);

  const handleActiveCheckRetry = () => {
    if (form.phone.length !== MAX_PHONE_DIGITS) return;
    lastCheckedPhoneRef.current = '';
    setActiveCheckTrigger((prev) => prev + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Валидация обязательных полей
    if (!form.name.trim() && !form.phone) {
      setError('Укажите имя и телефон клиента');
      return;
    }

    const assignedToUserId = form.assigned_to_user_id ?? profile?.id ?? null;
    if (!assignedToUserId) {
      setError('Не удалось определить пользователя для назначения заявки');
      return;
    }

    if (checkingActive) {
      setError('Дождитесь завершения проверки активных заявок');
      return;
    }

    if (activeCheckResult?.has_active) {
      setError('По указанному номеру уже есть активные заявки. Откройте существующую карточку.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim() || null,
        phone: form.phone || null,
        source_id: form.source_id,
        stage_code: DEFAULT_STAGE_CODE,
        phone_model: form.phone_model.trim() || null,
        memory_capacity: form.memory_capacity.trim() || null,
        battery_capacity_max: form.battery_capacity_max.trim() || null,
        phone_condition: form.phone_condition.trim() || null,
        has_box: form.has_box,
        assigned_to_user_id: assignedToUserId,
      };

      const created = await leadService.createLead(payload);
      showToast('Заявка создана', 'success', 1500);
      onClose();
      onCreated?.(created);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ошибка создания заявки';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const hasActiveConflict = Boolean(activeCheckResult?.has_active);
  const isSubmitDisabled = useMemo(
    () => submitting || loading || checkingActive || hasActiveConflict,
    [submitting, loading, checkingActive, hasActiveConflict]
  );
  const areFieldsDisabled = loading || submitting;
  const hasPrimaryInfo = useMemo(
    () => form.name.trim().length > 0 || form.phone.length > 0,
    [form.name, form.phone]
  );

  const formattedPhone = useMemo(() => formatPhoneDisplay(form.phone), [form.phone]);

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!submitting) onClose(); }} title="Новая заявка" size="lg">
      <form onSubmit={handleSubmit} className="lead-create-modal">
        <div className="lead-create-modal__hero">
          <div className="lead-create-modal__hero-icon" aria-hidden="true">
            <Sparkles size={28} />
          </div>
          <div className="lead-create-modal__hero-content">
            <p className="lead-create-modal__hero-title">Создаём новую заявку</p>
            <p className="lead-create-modal__hero-subtitle">
              Укажите контакт клиента и параметры устройства — дальше можно уточнить детали в процессе.
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error lead-create-modal__error" role="alert" aria-live="polite">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="lead-create-modal__loading" role="status">
            <div className="spinner" aria-hidden />
            <span>Загрузка справочников…</span>
          </div>
        ) : (
          <div className="lead-create-modal__grid">
            <div className="lead-create-modal__section">
              <div className="form-field">
                <label className="form-label" htmlFor={nameFieldId}>Имя клиента</label>
                <input
                  id={nameFieldId}
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Иванов Иван"
                  disabled={areFieldsDisabled}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor={phoneFieldId}>Телефон</label>
                <input
                  id={phoneFieldId}
                  className="input"
                  type="tel"
                  value={formattedPhone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: normalizePhoneDigits(e.target.value) }))}
                  placeholder="+7 (701) 000-11-22"
                  disabled={areFieldsDisabled}
                />
                {checkingActive && form.phone.length === MAX_PHONE_DIGITS && (
                  <div className="lead-create-modal__active-check" role="status">
                    <span className="spinner spinner-sm" aria-hidden />
                    <span>Проверяем активные заявки…</span>
                  </div>
                )}
                {activeCheckError && (
                  <div className="alert alert-error lead-create-modal__active-error" role="alert">
                    <AlertCircle size={16} />
                    <span>{activeCheckError}</span>
                    <button
                      type="button"
                      className="btn-ghost lead-create-modal__active-retry"
                      onClick={handleActiveCheckRetry}
                      disabled={checkingActive || form.phone.length !== MAX_PHONE_DIGITS}
                    >
                      Повторить проверку
                    </button>
                  </div>
                )}
                {hasActiveConflict && activeCheckResult && (
                  <div className="alert alert-warning lead-create-modal__active-alert" role="alert">
                    <div className="lead-create-modal__active-header">
                      <AlertCircle size={18} />
                      <div>
                        <p className="lead-create-modal__active-title">По номеру найдены активные заявки</p>
                        <p className="lead-create-modal__active-subtitle">Откройте существующую карточку, чтобы продолжить работу.</p>
                      </div>
                    </div>
                    <ul className="lead-create-modal__active-list">
                      {activeCheckResult.leads.map((leadItem) => (
                        <li key={leadItem.id}>
                          <button
                            type="button"
                            className="lead-create-modal__active-item"
                            onClick={() => handleActiveLeadOpen(leadItem.id)}
                          >
                            <div className="lead-create-modal__active-top">
                              <div className="lead-create-modal__active-main">
                                <span className="lead-create-modal__active-name">{leadItem.name || 'Без имени'}</span>
                                {leadItem.status_name && (
                                  <span className="badge badge-warning">{leadItem.status_name}</span>
                                )}
                              </div>
                              <span className="lead-create-modal__active-transition" aria-hidden="true">
                                <MoveRight size={18} />
                              </span>
                            </div>
                            <div className="lead-create-modal__active-meta">
                              <span>Заявка №{leadItem.id}</span>
                              {leadItem.assigned_to_full_name && (
                                <span>Ответственный: {leadItem.assigned_to_full_name}</span>
                              )}
                              {leadItem.created_by_full_name && (
                                <span>Создал: {leadItem.created_by_full_name}</span>
                              )}
                              {leadItem.phone && (
                                <span>Телефон: {leadItem.phone}</span>
                              )}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!hasActiveConflict && !checkingActive && activeCheckResult?.detail && (
                  <div className="alert alert-success lead-create-modal__active-success" role="status">
                    {activeCheckResult.detail}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor={sourceFieldId}>Источник</label>
                <select
                  id={sourceFieldId}
                  className="input"
                  value={form.source_id ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, source_id: e.target.value ? Number(e.target.value) : null }))}
                  disabled={areFieldsDisabled}
                >
                  <option value="">—</option>
                  {leadSources.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

            </div>

            <div className="lead-create-modal__section">
              <div className="form-field">
                <label className="form-label" htmlFor={modelFieldId}>Модель телефона</label>
                <input
                  id={modelFieldId}
                  className="input"
                  value={form.phone_model}
                  onChange={(e) => setForm((p) => ({ ...p, phone_model: e.target.value }))}
                  placeholder="iPhone 13 Pro"
                  disabled={areFieldsDisabled}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor={memoryFieldId}>Объём памяти</label>
                <input
                  id={memoryFieldId}
                  className="input"
                  value={form.memory_capacity}
                  onChange={(e) => setForm((p) => ({ ...p, memory_capacity: e.target.value }))}
                  placeholder="256 GB"
                  disabled={areFieldsDisabled}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor={batteryFieldId}>Ёмкость батареи (макс.)</label>
                <input
                  id={batteryFieldId}
                  className="input"
                  value={form.battery_capacity_max}
                  onChange={(e) => setForm((p) => ({ ...p, battery_capacity_max: e.target.value }))}
                  placeholder="100%"
                  disabled={areFieldsDisabled}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor={conditionFieldId}>Состояние телефона</label>
                <input
                  id={conditionFieldId}
                  className="input"
                  value={form.phone_condition}
                  onChange={(e) => setForm((p) => ({ ...p, phone_condition: e.target.value }))}
                  placeholder="Отличное"
                  disabled={areFieldsDisabled}
                />
              </div>

              <div className={`lead-create-modal__accessory ${form.has_box ? 'lead-create-modal__accessory--checked' : ''}`}>
                <label className="lead-create-modal__accessory-toggle" htmlFor={hasBoxFieldId}>
                  <input
                    id={hasBoxFieldId}
                    type="checkbox"
                    checked={form.has_box}
                    onChange={(e) => setForm((p) => ({ ...p, has_box: e.target.checked }))}
                    disabled={areFieldsDisabled}
                  />
                  <span className="lead-create-modal__accessory-icon" aria-hidden="true">
                    <Box size={18} />
                  </span>
                  <span className="lead-create-modal__accessory-content">
                    <span className="lead-create-modal__accessory-title">Есть оригинальная коробка</span>
                    <span className="lead-create-modal__accessory-hint">Добавим в карточку, чтобы оценщик учёл комплектацию</span>
                  </span>
                </label>
                <p className="lead-create-modal__accessory-note" aria-live="polite">
                  {form.has_box ? 'Отметка сохранится в истории заявки.' : 'Если коробки нет, просто оставьте выключенным.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions lead-create-modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitDisabled || !hasPrimaryInfo}>
            {submitting ? (<><span className="spinner" aria-hidden /> Создание…</>) : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default LeadCreateModal;
